import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetProviderConfigByIdQuery, useUpdateProviderConfigMutation, useGetProviderBalanceQuery } from '../../../store/api/providerConfigsApi';
import { formatDate, formatRelativeTime } from '../../../utils/dateFormatter';

export default function ProviderConfigDetailsPage() {
  const { t, i18n } = useTranslation();
  const { providerId } = useParams<{ providerId: string }>();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const configId = providerId || '';

  // Skip if providerId is "new" (should use AddProviderConfigPage instead)
  const { data, isLoading, error } = useGetProviderConfigByIdQuery(configId, {
    skip: !configId || configId === 'new',
  });
  const { data: balanceData, refetch: refetchBalance } = useGetProviderBalanceQuery(configId, {
    skip: !configId || configId === 'new',
  });

  const [updateConfig, { isLoading: isUpdating }] = useUpdateProviderConfigMutation();
  const isPatching = false;

  const config = data?.data?.config;
  const balance = balanceData?.data?.balance;

  // Form states
  const [apiKey, setApiKey] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [margins, setMargins] = useState<Array<{ key: string; value: string }>>([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (config) {
      setBaseUrl(config.endpoints?.baseUrl || '');
      if (config.margins) {
        const marginsArray = Object.entries(config.margins).map(([key, value]) => ({
          key,
          value: (value * 100).toFixed(2), // Convert to percentage for display
        }));
        setMargins(marginsArray);
      } else {
        setMargins([]);
      }
    }
  }, [config]);

  const handleUpdate = async () => {
    if (!config) return;

    try {
      const marginsObj: Record<string, number> = {};
      margins.forEach(({ key, value }) => {
        const trimmedKey = key.trim();
        const numValue = parseFloat(value);
        if (trimmedKey && !isNaN(numValue) && numValue >= 0 && numValue <= 1000) {
          marginsObj[trimmedKey] = numValue / 100; // Convert back to decimal
        }
      });

      // Build request body - only include fields that have values
      const requestBody: {
        providerId: string;
        apiKeys?: { apiKey: string };
        endpoints?: { baseUrl: string };
        margins?: Record<string, number>;
      } = {
        providerId: configId,
      };

      if (apiKey.trim()) {
        requestBody.apiKeys = { apiKey: apiKey.trim() };
      }

      if (baseUrl.trim()) {
        requestBody.endpoints = { baseUrl: baseUrl.trim() };
      }

      if (Object.keys(marginsObj).length > 0) {
        requestBody.margins = marginsObj;
      }

      await updateConfig({
        providerId: configId,
        data: requestBody,
      }).unwrap();

      toast.success(t('admin.providerConfigs.updateSuccess') || 'Provider configuration updated successfully');
      setIsEditing(false);
      setApiKey(''); // Clear API key after update
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update provider configuration');
    }
  };

  const handleAddMargin = () => {
    setMargins([...margins, { key: '', value: '' }]);
  };

  const handleRemoveMargin = (index: number) => {
    setMargins(margins.filter((_, i) => i !== index));
  };

  const handleMarginKeyChange = (index: number, newKey: string) => {
    const newMargins = [...margins];
    const current = newMargins[index] ?? { key: '', value: '' };
    newMargins[index] = { ...current, key: newKey };
    setMargins(newMargins);
  };

  const handleMarginValueChange = (index: number, newValue: string) => {
    const newMargins = [...margins];
    const current = newMargins[index] ?? { key: '', value: '' };
    newMargins[index] = { ...current, value: newValue };
    setMargins(newMargins);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !config) {
    return (
      <div className="text-center py-12">
        <p className="text-danger mb-4">
          {t('admin.providerConfigs.details.error.title') || 'Error loading provider configuration'}
        </p>
        <p className="text-neutral-medium mb-6">
          {t('admin.providerConfigs.details.error.notFound') || 'Provider configuration not found'}
        </p>
        <Link
          to="/admin/provider-configs"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200"
        >
          {t('admin.providerConfigs.details.back') || 'Back to Provider Configs'}
        </Link>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div>
          <Link
            to="/admin/provider-configs"
            className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('admin.providerConfigs.details.back') || 'Back to Provider Configs'}
          </Link>
          <h1 className="text-3xl font-heading font-bold text-brand-accent mb-2">
            {t('admin.providerConfigs.details.title') || 'Provider Configuration'}
          </h1>
          <p className="text-neutral-medium">
            {t('admin.providerConfigs.details.info.providerId') || 'Provider ID'}:{' '}
            <span className="font-mono text-neutral-light">{config.providerId}</span>
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-3">
          {!isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
              >
                {t('admin.providerConfigs.actions.edit') || 'Edit'}
              </button>
              <button
                onClick={() => {
                  void refetchBalance();
                }}
                className="px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/50 font-heading font-semibold rounded-lg hover:bg-blue-500/30 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
              >
                {t('admin.providerConfigs.actions.checkBalance') || 'Check Balance'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  // Reset form
                  setBaseUrl(config.endpoints?.baseUrl || '');
                  if (config.margins) {
                    const marginsArray = Object.entries(config.margins).map(([key, value]) => ({
                      key,
                      value: (value * 100).toFixed(2),
                    }));
                    setMargins(marginsArray);
                  } else {
                    setMargins([]);
                  }
                  setApiKey('');
                }}
                className="px-4 py-2 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={() => {
                  void handleUpdate();
                }}
                disabled={isUpdating || isPatching}
                className="px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating || isPatching
                  ? t('common.saving') || 'Saving...'
                  : t('common.save') || 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Configuration Information */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.1s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.providerConfigs.details.info.title') || 'Configuration Information'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.providerConfigs.details.info.providerId') || 'Provider ID'}
                </label>
                <p className="text-neutral-light font-mono">{config.providerId}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.providerConfigs.details.info.baseUrl') || 'Base URL'}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://smmworld.org/api/v2"
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                    disabled={isUpdating || isPatching}
                  />
                ) : (
                  <p className="text-neutral-light">{config.endpoints?.baseUrl || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.providerConfigs.details.info.apiKey') || 'API Key'}
                </label>
                {isEditing ? (
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={t('admin.providerConfigs.details.info.apiKeyPlaceholder') || 'Enter API key (leave empty to keep current)'}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                    disabled={isUpdating || isPatching}
                  />
                ) : (
                  <p className="text-neutral-light font-mono">
                    {config.apiKeys?.apiKey === '[ENCRYPTED]' ? '[ENCRYPTED]' : '••••••••'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.providerConfigs.details.info.margins') || 'Margins (%)'}
                </label>
                {isEditing ? (
                  <div className="space-y-2">
                    {margins.map((margin, index) => (
                      <div key={index} className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={margin.key}
                          onChange={(e) => handleMarginKeyChange(index, e.target.value)}
                          placeholder="social_en"
                          className="flex-1 px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                          disabled={isUpdating || isPatching}
                        />
                        <div className="flex gap-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="1000"
                            value={margin.value}
                            onChange={(e) => handleMarginValueChange(index, e.target.value)}
                            placeholder="15.0"
                            className="w-full sm:w-24 px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                            disabled={isUpdating || isPatching}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveMargin(index)}
                            disabled={isUpdating || isPatching}
                            className="px-3 sm:px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                          >
                            {t('admin.providerConfigs.details.info.removeMargin') || 'Remove'}
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddMargin}
                      disabled={isUpdating || isPatching}
                      className="px-4 py-2 bg-neutral-medium text-neutral-light rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {t('admin.providerConfigs.details.info.addMargin') || 'Add Margin'}
                    </button>
                  </div>
                ) : (
                  <div>
                    {config.margins && Object.keys(config.margins).length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(config.margins).map(([key, value]) => (
                          <span
                            key={key}
                            className="px-3 py-1 text-sm bg-brand-accent/20 text-brand-accent rounded border border-brand-accent/50"
                          >
                            {key}: {(value * 100).toFixed(1)}%
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-neutral-medium">-</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.providerConfigs.details.info.created') || 'Created At'}
                </label>
                <p className="text-neutral-light">{formatDate(config.createdAt, 'PP', currentLanguage)}</p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(config.createdAt, currentLanguage)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.providerConfigs.details.info.updated') || 'Updated At'}
                </label>
                <p className="text-neutral-light">{formatDate(config.updatedAt, 'PP', currentLanguage)}</p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(config.updatedAt, currentLanguage)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Balance Card */}
          {balance && (
            <div
              className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
              style={{
                animation: 'slideUp 0.5s ease-out 0.2s both',
              }}
            >
              <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
                {t('admin.providerConfigs.details.balance.title') || 'Provider Balance'}
              </h2>
              <div className="space-y-2">
                <p className="text-2xl font-heading font-bold text-brand-accent">
                  {balance.balance} {balance.currency}
                </p>
                <button
                  onClick={() => {
                    void refetchBalance();
                  }}
                  className="w-full mt-4 px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-all duration-200 text-sm"
                >
                  {t('admin.providerConfigs.details.balance.refresh') || 'Refresh Balance'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

